import CheckIcon from '@mui/icons-material/Check';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { styled } from '@mui/material/styles';
import { Avatar } from '@bloomays-lib/ui.shared';
import { useTranslation } from 'react-i18next';
import {
  AnnualDealsGoalState,
  AnnualGoalHeaderState,
  AnnualCurrentMonthHeaderState,
  isCurrentYear,
} from '../../utils/annualPrime.utils';
import { formatPrimeEuro } from '../../utils/monthlyPrime.utils';
import PrimeGoalProgressKpi from './PrimeGoalProgressKpi';
import PrimeValueKpi from './PrimeValueKpi';
import AnnualYearNavigator from './AnnualYearNavigator';
import DealsGoalEligibilityBanner from './DealsGoalEligibilityBanner';
import PrimesPeriodToggle from './PrimesPeriodToggle';

export type AnnualTamPrimesHeaderProps = {
  tamDisplayName: string;
  tamEmail: string;
  tamSeniority?: string;
  tamAvatarUrl?: string | null;
  selectedYear: string;
  onSelectedYearChange: (year: string) => void;
  currentMonth?: AnnualCurrentMonthHeaderState | null;
  goalState?: AnnualGoalHeaderState | null;
  dealsState?: AnnualDealsGoalState | null;
  isTamPreview?: boolean;
  lifetimeMarginAmount?: string;
  lifetimeMarginSubtitle?: string;
  loading: boolean;
};

const AnnualTamPrimesHeader = ({
  tamDisplayName,
  tamEmail,
  tamSeniority,
  tamAvatarUrl,
  selectedYear,
  onSelectedYearChange,
  currentMonth,
  goalState,
  dealsState,
  isTamPreview = false,
  lifetimeMarginAmount,
  lifetimeMarginSubtitle,
  loading,
}: AnnualTamPrimesHeaderProps) => {
  const { t } = useTranslation(['primes']);

  const showCurrentMonthStatus = isCurrentYear(selectedYear);
  const formatAmount = (value: number | undefined) => (loading ? '...' : formatPrimeEuro(value ?? 0));
  const seniorityLabel = tamSeniority?.trim() || '—';
  const dealsValuePrimary = loading ? '...' : String(dealsState?.signedDeals ?? 0);
  const dealsValueSecondary = loading
    ? '...'
    : dealsState?.isMaxSeniority
    ? t('dealsGoalExecutiveState')
    : `/ ${t('annualHeaderDealsTarget', { count: dealsState?.targetThreshold ?? 0 })}`;

  return (
    <HeaderWrapper>
      <ToolbarRow>
        <PrimesPeriodToggle activeView="annual" tamEmail={tamEmail} selectedYear={selectedYear} />
        <YearNavigatorWrapper>
          <AnnualYearNavigator selectedYear={selectedYear} onSelectedYearChange={onSelectedYearChange} />
        </YearNavigatorWrapper>
      </ToolbarRow>

      {dealsState && !isTamPreview ? (
        <DealsGoalEligibilityBanner
          currentSeniority={tamSeniority ?? ''}
          selectedYear={selectedYear}
          dealsState={dealsState}
        />
      ) : null}

      <HeaderShell>
        <HeaderContainer>
          <ProfileRow>
            <ProfileIdentity>
              <ProfileAvatar>
                <Avatar size="medium" alt={tamDisplayName || '—'} image={tamAvatarUrl} />
              </ProfileAvatar>
              <IdentityTextBlock>
                <NameRow>
                  <TamName>{tamDisplayName || '—'}</TamName>
                  <SeniorityBadge title={t('seniorityLabel')}>
                    <WorkspacePremiumIcon sx={{ fontSize: 13 }} />
                    {seniorityLabel}
                  </SeniorityBadge>
                </NameRow>
                <TamEmail>{tamEmail || '—'}</TamEmail>
              </IdentityTextBlock>
            </ProfileIdentity>

            <ProfileActions>
              {showCurrentMonthStatus && currentMonth ? (
                currentMonth.status === 'validated' ? (
                  <StatusPill $tone="validated">
                    <CheckIcon sx={{ fontSize: 13 }} />
                    {t('monthStatusValidated')}
                  </StatusPill>
                ) : (
                  <StatusPill $tone="pending">{t('monthStatusPending')}</StatusPill>
                )
              ) : null}
            </ProfileActions>
          </ProfileRow>

          <KpiStrip>
            <PrimeGoalProgressKpi
              title={t('annualHeaderMarginGoalTitle')}
              valuePrimary={formatAmount(goalState?.cumulativeMargin)}
              valueSecondary={`/ ${formatAmount(goalState?.targetAmount)}`}
              percent={goalState?.percent ?? 0}
              progressBarPercent={goalState?.progressBarPercent ?? 0}
              accent="green"
              loading={loading}
            />

            <KpiDivider />

            <PrimeGoalProgressKpi
              title={t('annualHeaderDealsGoalTitle')}
              valuePrimary={dealsValuePrimary}
              valueSecondary={dealsValueSecondary}
              percent={dealsState?.percent ?? 0}
              progressBarPercent={dealsState?.progressBarPercent ?? 0}
              accent="purple"
              loading={loading}
              showProgress={!dealsState?.isMaxSeniority}
              exceededBadge={dealsState?.isExceeded ? t('dealsGoalExceededBadge') : undefined}
            />

            <KpiDivider />

            <PrimeValueKpi
              title={t('annualHeaderLifetimeMarginTitle')}
              value={lifetimeMarginAmount ?? formatAmount(0)}
              subtitle={
                lifetimeMarginSubtitle
                  ? t('annualHeaderLifetimeMarginSince', { monthYear: lifetimeMarginSubtitle })
                  : undefined
              }
              loading={loading}
              variant="default"
            />

            {showCurrentMonthStatus ? (
              <>
                <KpiDivider />
                <PrimeValueKpi
                  title={t('annualHeaderToReceive')}
                  value={formatAmount(currentMonth?.totalPrime)}
                  subtitle={
                    currentMonth?.monthName
                      ? t('annualHeaderToReceiveMonthSubtitle', { month: currentMonth.monthName })
                      : undefined
                  }
                  variant="highlight"
                  loading={loading}
                />
              </>
            ) : null}
          </KpiStrip>
        </HeaderContainer>
      </HeaderShell>
    </HeaderWrapper>
  );
};

export default AnnualTamPrimesHeader;

const HeaderWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(3),
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
}));

const HeaderShell = styled('div')(() => ({
  borderRadius: '18px',
  boxShadow: '0 8px 24px rgba(75, 75, 75, 0.12)',
}));

const HeaderContainer = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.neutral.white,
  borderRadius: '18px',
  overflow: 'hidden',
  // body { text-align: center } in main.css — inline text (email) would inherit it otherwise.
  textAlign: 'left',
}));

const ToolbarRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const YearNavigatorWrapper = styled('div')(() => ({
  marginLeft: 'auto',
}));

const ProfileRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2.5),
  flexWrap: 'wrap',
  padding: '17px 24px',
}));

const ProfileIdentity = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '13px',
  minWidth: 0,
  flex: '1 1 280px',
}));

const ProfileAvatar = styled('div')(() => ({
  flexShrink: 0,
  '& .MuiAvatar-root': {
    width: 46,
    height: 46,
    fontSize: 15,
  },
}));

const IdentityTextBlock = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
  minWidth: 0,
}));

const NameRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '9px',
}));

const TamName = styled('span')(({ theme }) => ({
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: '15.5px',
  fontWeight: 600,
  color: theme.palette.neutral[90],
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: 280,
}));

const SeniorityBadge = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '5px',
  padding: '3px 10px 3px 8px',
  borderRadius: 999,
  backgroundColor: theme.palette.purple[20],
  color: theme.palette.purple[100],
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: '11.5px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  flexShrink: 0,
}));

const TamEmail = styled('span')(({ theme }) => ({
  fontFamily: '"Poppins Regular", sans-serif',
  fontSize: '13px',
  color: theme.palette.neutral[60],
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: 320,
}));

const ProfileActions = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.75),
  flexWrap: 'wrap',
  flexShrink: 0,
}));

const StatusPill = styled('span', {
  shouldForwardProp: (prop) => prop !== '$tone',
})<{ $tone: 'pending' | 'validated' }>(({ theme, $tone }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 13px',
  borderRadius: 999,
  fontFamily: '"Poppins Medium", sans-serif',
  fontSize: '12.5px',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  ...($tone === 'validated'
    ? {
        backgroundColor: theme.palette.green[20],
        color: theme.palette.green[100],
      }
    : {
        backgroundColor: theme.palette.beige[40],
        color: theme.palette.beige[100],
      }),
}));

const KpiStrip = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'stretch',
  borderTop: `1px solid ${theme.palette.neutral[15]}`,
  backgroundColor: theme.palette.neutral[10],
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const KpiDivider = styled('span')(({ theme }) => ({
  width: 1,
  backgroundColor: theme.palette.neutral[20],
  margin: '18px 0',
  flexShrink: 0,
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));
